import React, { useState, useEffect } from "react";
import { Button, Badge } from "../../components/ui";
import { useTranslation } from "react-i18next";
import {
  Wifi,
  Loader2,
  Monitor,
  Smartphone,
  Wifi as Router,
  Tv,
  Home,
  Plus,
  RefreshCw,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useProperty } from "../../contexts/PropertyContext";
import {
  scanNetwork,
  getNetworkHistory,
  registerDeviceAsAsset,
  type NetworkDevice,
  type NetworkScanResult,
} from "../../services/discovery.api";
import { useNavigate } from "react-router-dom";

// Device type icon mapping
const DEVICE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  router: Router,
  computer: Monitor,
  phone: Smartphone,
  tv: Tv,
  default: Home,
};

function getDeviceIcon(deviceType?: string) {
  if (!deviceType) return DEVICE_ICONS.default;
  const type = deviceType.toLowerCase();
  return DEVICE_ICONS[type] || DEVICE_ICONS.default;
}

export function Discovery() {
  const { t } = useTranslation('discovery');
  const { currentProperty } = useProperty();
  const _navigate = useNavigate();

  const [isScanning, setIsScanning] = useState(false);
  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [scanHistory, setScanHistory] = useState<NetworkScanResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Load scan history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const history = await getNetworkHistory();
      setScanHistory(history);
      
      // Load most recent scan devices
      if (history.length > 0) {
        setDevices(history[0].devices);
      }
    } catch (err: unknown) {
      console.error('Failed to load history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleScan = async () => {
    setIsScanning(true);
    setError(null);

    try {
      const result = await scanNetwork();
      setDevices(result.devices);
      setScanHistory([result, ...scanHistory]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('error.scanFailed'));
    } finally {
      setIsScanning(false);
    }
  };

  const handleRegisterDevice = async (device: NetworkDevice) => {
    if (!currentProperty?.id) {
      setError(t('error.noHome'));
      return;
    }

    setRegistering(device.ip);
    setError(null);

    try {
      await registerDeviceAsAsset(currentProperty.id, device);
      
      // Update device list to show it's registered
      setDevices(prevDevices =>
        prevDevices.map(d =>
          d.ip === device.ip ? { ...d, isRegistered: true } : d
        )
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('error.registerFailed'));
    } finally {
      setRegistering(null);
    }
  };

  // Filter devices by search query
  const filteredDevices = devices.filter(device => {
    const query = searchQuery.toLowerCase();
    return (
      device.hostname?.toLowerCase().includes(query) ||
      device.ip.includes(query) ||
      device.vendor?.toLowerCase().includes(query) ||
      device.deviceType?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h1 className="text-4xl font-light tracking-tight mb-2">
                {t('title')}
              </h1>
              <p className="text-muted-foreground">
                {t('subtitle')}
              </p>
            </div>
            <Button
              onClick={handleScan}
              disabled={isScanning}
              size="lg"
              className="gap-2"
            >
              {isScanning ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('scanning')}
                </>
              ) : (
                <>
                  <Wifi className="h-5 w-5" />
                  {t('scan')}
                </>
              )}
            </Button>
          </div>

          {/* Stats */}
          <div className="flex gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm">
              <span className="font-medium">
                {t('stats.device', { count: devices.length })}
              </span>
            </div>
            {devices.some((d: NetworkDevice & { isRegistered?: boolean }) => d.isRegistered) && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                <span className="font-medium">
                  {t('stats.registered', { count: devices.filter((d: NetworkDevice & { isRegistered?: boolean }) => d.isRegistered).length })}
                </span>
              </div>
            )}
          </div>

          {/* Search */}
          {devices.length > 0 && (
            <div className="mt-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('search.placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/30 p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-600">{error}</p>
            </div>
          </div>
        )}

        {isScanning ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
              <Wifi className="h-8 w-8 text-primary animate-pulse" />
            </div>
            <h3 className="text-xl font-normal mb-2">{t('state.scanning.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('state.scanning.desc')}
            </p>
          </div>
        ) : filteredDevices.length === 0 && devices.length === 0 ? (
          <div className="text-center py-24">
            <div className="h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
              <Wifi className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-normal mb-2">{t('state.empty.title')}</h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              {t('state.empty.desc')}
            </p>
            <Button onClick={handleScan} className="mt-6 gap-2">
              <Wifi className="h-4 w-4" />
              {t('startScan')}
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredDevices.map((device, index) => {
              const Icon = getDeviceIcon(device.deviceType);
              const isRegistered = (device as NetworkDevice & { isRegistered?: boolean }).isRegistered;
              const isRegistering = registering === device.ip;

              return (
                <article
                  key={device.ip}
                  className={cn(
                    "rounded-2xl border bg-card p-6 transition-all hover:shadow-lg",
                    "opacity-0 animate-fade-in-up"
                  )}
                  style={{
                    animationDelay: `${index * 0.05}s`,
                    animationFillMode: "forwards",
                  }}
                >
                  {/* Icon & Status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-xl border bg-background flex items-center justify-center">
                      <Icon className="h-6 w-6 text-muted-foreground" />
                    </div>
                    {isRegistered ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {t('device.registered')}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className={device.isOnline ? "bg-green-50 text-green-700 border-green-200" : ""}>
                        {device.isOnline ? t('device.online') : t('device.offline')}
                      </Badge>
                    )}
                  </div>

                  {/* Info */}
                  <div className="space-y-2 mb-4">
                    <h3 className="font-semibold text-base leading-tight">
                      {device.hostname || t('device.unknown')}
                    </h3>
                    <p className="text-sm text-muted-foreground font-mono">
                      {device.ip}
                    </p>
                    {device.vendor && (
                      <p className="text-xs text-muted-foreground">
                        {device.vendor}
                        {device.model && ` • ${device.model}`}
                      </p>
                    )}
                    {device.deviceType && (
                      <Badge variant="secondary" className="text-xs">
                        {device.deviceType}
                      </Badge>
                    )}
                  </div>

                  {/* Actions */}
                  {!isRegistered && (
                    <Button
                      size="sm"
                      variant="default"
                      className="w-full gap-2"
                      onClick={() => handleRegisterDevice(device)}
                      disabled={isRegistering}
                    >
                      {isRegistering ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {t('device.registering')}
                        </>
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5" />
                          {t('device.register')}
                        </>
                      )}
                    </Button>
                  )}

                  {/* Metadata */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(device.lastSeen).toLocaleString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Scan History */}
        {scanHistory.length > 0 && (
          <div className="mt-12 pt-8 border-t">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('history.title')}</h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadHistory}
                disabled={isLoadingHistory}
              >
                <RefreshCw className={cn("h-3.5 w-3.5", isLoadingHistory && "animate-spin")} />
              </Button>
            </div>
            <div className="space-y-3">
              {scanHistory.slice(0, 5).map((scan, _idx) => (
                <div
                  key={scan.scanId}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border bg-card hover:bg-accent transition-colors cursor-pointer"
                  onClick={() => setDevices(scan.devices)}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg border bg-background flex items-center justify-center">
                      <Wifi className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {t('history.found', { count: scan.devicesFound })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(scan.devices[0]?.lastSeen || Date.now()).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {(scan.scanDuration / 1000).toFixed(1)}s
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
